(() => {
  if (customElements.get('box-mix-builder')) return;
  class BoxMixBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = document.getElementById(this.dataset.formId);
      const config = this.querySelector('[data-mix-config]');
      if (!this.form || !config) return;
      try { this.config = JSON.parse(config.textContent); } catch { return; }
      this.targetWeight = this.config.targetWeight === 500 ? 500 : 1000;
      this.maxFlavors = this.targetWeight / 100;
      this.maxPerFlavor = this.targetWeight === 500 ? 100 : 1000;
      this.initialized = true;
      this.allocations = this.allocations || new Map();
      const allowedKeys = new Set(this.config.flavors.flatMap((flavor) => this.coatingOptions(flavor).map((coating) => this.portionKey(flavor.id, coating))));
      this.allocations.forEach((_, key) => { if (!allowedKeys.has(key)) this.allocations.delete(key); });
      this.elsaVariantId = this.elsaVariantId || this.variant()?.id;
      this.mode = this.mode || 'elsa';
      this.filter = this.filter || 'all';
      this.view = this.view || 'browse';
      this.list = this.querySelector('[data-mix-list]');
      this.selections = this.querySelector('[data-mix-selections]');
      this.search = this.querySelector('[data-mix-search]');
      this.error = this.querySelector('[data-mix-error]');
      this.summarySubmit = this.querySelector('[data-mix-submit]');
      this.nativeSubmit = this.form.querySelector('[name="add"]');
      this.setupSubmitGate();
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.closest('.atelier-box-flow')?.querySelectorAll('[data-box-photos-open]').forEach((link) => {
        link.addEventListener('click', () => { const gallery = document.querySelector(link.getAttribute('href')); if (gallery) gallery.open = true; }, {signal});
      });
      this.form.addEventListener('submit', (event) => this.validateSubmit(event), {capture: true, signal});
      this.form.addEventListener('change', (event) => {
        if (event.target.name !== 'id' || this.syncingNativeVariant) return;
        if (this.mode === 'elsa') this.elsaVariantId = this.variant()?.id;
        this.update();
      }, {signal});
      this.form.addEventListener('reset', () => queueMicrotask(() => {
        this.mode = 'elsa';
        this.view = 'browse';
        this.allocations.clear();
        this.syncMode();
      }), {signal});
      this.addEventListener('change', (event) => {
        if (event.target.matches('.box-mix__modes input[type=radio]')) {
          this.mode = event.target.value;
          this.syncMode();
        }
      }, {signal});
      this.addEventListener('input', (event) => {
        if (event.target === this.search) this.filterCards();
      }, {signal});
      this.addEventListener('click', (event) => this.handleClick(event), {signal});
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-box-mix-open]');
        if (!link || link.getAttribute('href') !== `#${this.id}` || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.mode = 'custom';
        this.syncMode();
        this.setView('browse', true);
      }, {signal});
      this.nativeObserver = new MutationObserver(() => this.syncSubmitControl(this.status()));
      if (this.nativeSubmit) this.nativeObserver.observe(this.nativeSubmit, {attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class']});
      this.renderRows();
      this.querySelector('.box-mix__interactive').hidden = false;
      this.syncMode();
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.nativeObserver?.disconnect();
      if (this.submitGate) this.submitGate.disabled = false;
      this.initialized = false;
    }
    setupSubmitGate() {
      this.submitGate = this.form.querySelector('[data-box-mix-submit-gate]');
      if (this.submitGate) return;
      const buttons = this.form.querySelector('.product-form__buttons') || this.nativeSubmit;
      if (!buttons) return;
      this.submitGate = document.createElement('fieldset');
      this.submitGate.dataset.boxMixSubmitGate = '';
      this.submitGate.style.display = 'contents';
      buttons.before(this.submitGate);
      this.submitGate.append(buttons);
    }
    text(key, variables = {}) {
      return Object.entries(variables).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.config.labels[key] || '');
    }
    variant() {
      const id = this.form.querySelector('input[name="id"]')?.value;
      return this.config.variants.find((variant) => String(variant.id) === String(id));
    }
    coatingOptions(flavor) {
      const known = ['milk', 'dark', 'caramel', 'strawberry', 'white', 'original'];
      return Array.isArray(flavor?.coatingOptions) ? flavor.coatingOptions.filter((value) => known.includes(value)) : [];
    }
    portionKey(id, coating) { return `${id}:${coating}`; }
    displayName(flavor) { return `${flavor.id}. ${flavor.name}`; }
    hasFlavor(id) { return [...this.allocations.keys()].some((key) => key.startsWith(`${id}:`)); }
    coatingLabel(flavor, coating = this.coatingOptions(flavor)[0]) {
      return this.coatingOptions(flavor).includes(coating) ? this.text(`coating_${coating}`) : '';
    }
    portionEntries() {
      return [...this.allocations].map(([key, raw]) => {
        const [id, coating] = key.split(':');
        return {key, flavor: this.config.flavors.find((flavor) => flavor.id === id), coating, grams: Number(raw), raw};
      });
    }
    requiredVariant() {
      const coatings = this.portionEntries().filter((entry) => entry.grams > 0).map((entry) => entry.coating);
      const type = coatings.length && coatings.every((coating) => coating === 'dark') ? 'dark'
        : coatings.length && coatings.every((coating) => coating === 'milk') ? 'milk' : 'mixed';
      return this.config.variants.find((variant) => variant.type === type);
    }
    selectNativeVariant(variant) {
      if (!variant || this.syncingNativeVariant) return false;
      const picker = this.querySelector('variant-radios, variant-selects');
      if (!picker) return false;
      let variants;
      try {
        variants = typeof picker.getVariantData === 'function' ? picker.getVariantData() : JSON.parse(picker.querySelector('[type="application/json"]')?.textContent || '[]');
      } catch { return false; }
      if (!Array.isArray(variants)) return false;
      const nativeVariant = variants.find((item) => String(item.id) === String(variant.id));
      if (!Array.isArray(nativeVariant?.options)) return false;
      const radios = picker.matches('variant-radios');
      const groups = [...picker.querySelectorAll(radios ? 'fieldset' : 'select')];
      if (groups.length !== nativeVariant.options.length) return false;
      const choices = groups.map((group, index) => [...group.querySelectorAll(radios ? 'input[type="radio"]' : 'option')].find((choice) => choice.value === nativeVariant.options[index]));
      if (!groups.length || choices.some((choice) => !choice)) return false;
      const selected = groups.every((group, index) => radios ? choices[index].checked : group.value === nativeVariant.options[index]);
      if (selected && String(this.variant()?.id) === String(variant.id) && (!picker.currentVariant || String(picker.currentVariant.id) === String(variant.id))) return true;
      this.syncingNativeVariant = true;
      try {
        groups.forEach((group, index) => {
          if (radios) group.querySelectorAll('input[type="radio"]').forEach((radio) => { radio.checked = radio === choices[index]; });
          else group.value = nativeVariant.options[index];
        });
        // Use the theme's own variant change path: it owns the hidden ID, stock, media and price.
        (radios ? choices[choices.length - 1] : groups[groups.length - 1]).dispatchEvent(new Event('change', {bubbles: true}));
      } finally { this.syncingNativeVariant = false; }
      return String(this.variant()?.id) === String(variant.id);
    }
    allowed(flavor, variant = this.variant()) {
      if (!flavor) return false;
      if (this.mode === 'custom') return true;
      if (!variant || !variant.available) return false;
      if (variant.type === 'mixed') return true;
      return variant.type === 'dark' ? flavor.dark : variant.type === 'milk' && flavor.milk;
    }
    status() {
      const variant = this.variant();
      const entries = this.portionEntries();
      const total = entries.reduce((sum, entry) => sum + (Number.isFinite(entry.grams) ? entry.grams : 0), 0);
      const selected = entries.filter((entry) => entry.grams > 0);
      const flavorCount = new Set(selected.map((entry) => entry.flavor?.id)).size;
      let error = '';
      const requiredVariant = this.mode === 'custom' ? this.requiredVariant() : variant;
      if (!variant || !variant.available || variant.type === 'unknown' || !requiredVariant || String(variant.id) !== String(requiredVariant.id)) error = 'invalid_variant';
      else if (this.mode === 'custom') {
        if (entries.some((entry) => entry.raw === '' || !Number.isFinite(entry.grams) || !Number.isInteger(entry.grams) || entry.grams < 0 || entry.grams > this.maxPerFlavor || entry.grams % 100 !== 0)) error = 'invalid_amount';
        else if (entries.some((entry) => !entry.flavor)) error = 'invalid_flavor';
        else if (selected.some((entry) => !this.coatingOptions(entry.flavor).includes(entry.coating))) error = 'invalid_coating';
        else if (flavorCount > this.maxFlavors) error = 'limit';
        else if (total < this.targetWeight) error = 'remaining';
        else if (total > this.targetWeight) error = 'over';
      }
      return {variant, entries, selected, flavorCount, total, error, valid: !error};
    }
    syncMode() {
      if (this.mode === 'custom' && this.activeMode === 'elsa') this.elsaVariantId = this.variant()?.id;
      if (this.mode === 'elsa' && this.activeMode === 'custom') this.selectNativeVariant(this.config.variants.find((variant) => String(variant.id) === String(this.elsaVariantId)));
      this.activeMode = this.mode;
      this.dataset.mode = this.mode;
      this.querySelectorAll('.box-mix__native-type, [data-mix-global-type]').forEach((element) => { element.hidden = this.mode === 'custom'; });
      this.querySelectorAll('[data-mix-custom-type]').forEach((element) => { element.hidden = this.mode !== 'custom'; });
      this.querySelectorAll('.box-mix__modes input[type=radio]').forEach((input) => { input.checked = input.value === this.mode; });
      this.querySelector('[data-mix-custom]').hidden = this.mode !== 'custom';
      this.querySelector('[data-mix-elsa-description]').hidden = this.mode !== 'elsa';
      this.setView(this.view);
      this.update();
    }
    setView(view, focus = false) {
      this.view = view;
      this.dataset.view = view;
      this.querySelector('[data-mix-catalog]').hidden = view === 'review';
      this.querySelectorAll('.box-mix__steps [data-mix-view]').forEach((button) => {
        if (button.dataset.mixView === view) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      if (focus) this.focusStart(view);
    }
    focusStart(view = 'browse') {
      const heading = this.querySelector(view === 'review' ? '[data-mix-tray] h3' : '[data-mix-catalog] h3');
      heading?.focus({preventScroll: true});
      heading?.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    }
    returnToDetails(event, link) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const details = document.getElementById(link.hash.slice(1));
      if (!details) return;
      event.preventDefault();
      const variant = this.mode === 'custom' ? this.querySelector('.box-mix__modes input:checked') : details.querySelector('variant-selects input:checked, variant-radios input:checked, variant-selects select, select[name="id"]');
      const target = variant || details;
      if (!variant) target.setAttribute('tabindex', '-1');
      target.focus({preventScroll: true});
      details.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    button(className, label, action, flavorId, text) {
      const button = this.element('button', className, text);
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.dataset.mixAction = action;
      button.dataset.flavorId = flavorId;
      return button;
    }
    controls(flavor, suffix, coating = this.coatingOptions(flavor)[0]) {
      const key = this.portionKey(flavor.id, coating);
      const controls = this.element('div', 'box-mix__controls');
      controls.dataset.mixPortion = key;
      const display = this.element('span', 'box-mix__grams', this.allocations.get(key) || '0');
      display.id = `BoxMixGrams-${this.dataset.sectionId}-${flavor.id}-${coating}-${suffix}`;
      display.dataset.mixGrams = key;
      controls.setAttribute('role', 'group');
      const name = `${this.displayName(flavor)} — ${this.coatingLabel(flavor, coating)}`;
      controls.setAttribute('aria-label', this.text('grams_label', {flavor: name}));
      controls.setAttribute('aria-describedby', `BoxMixDescription-${this.dataset.sectionId}-${flavor.id} BoxMixInstructions-${this.dataset.sectionId}`);
      const amount = this.element('span', 'box-mix__amount');
      amount.append(display, this.element('span', 'box-mix__unit', this.text('grams')));
      const decrease = this.button('box-mix__step', this.text('decrease', {flavor: name}), 'decrease', flavor.id, '−');
      const increase = this.button('box-mix__step', this.text('increase', {flavor: name}), 'increase', flavor.id, '+');
      decrease.dataset.coating = increase.dataset.coating = coating;
      controls.append(decrease, amount, increase);
      if (this.coatingOptions(flavor).length > 1) {
        const add = this.button('box-mix__coating-add', this.text('increase', {flavor: name}), 'increase', flavor.id, this.text('add'));
        add.dataset.mixCoatingAdd = key;
        add.dataset.coating = coating;
        add.id = `${display.id}-add`;
        controls.append(add);
      }
      return controls;
    }
    coatingControls(flavor, suffix) {
      const wrapper = this.element('div', 'box-mix__coating');
      const options = this.coatingOptions(flavor);
      if (options.length > 1) {
        wrapper.classList.add('box-mix__dual');
        options.forEach((coating) => {
          const group = this.element('div', 'box-mix__coating-portion');
          const controls = this.controls(flavor, suffix, coating);
          const label = this.element('span', 'box-mix__coating-label', this.text(`coating_option_${coating}`) || this.coatingLabel(flavor, coating));
          group.append(label, controls);
          wrapper.append(group);
        });
      } else {
        wrapper.hidden = options[0] === 'original';
        wrapper.append(this.element('p', 'box-mix__coating-note', this.coatingLabel(flavor)));
      }
      return wrapper;
    }
    photo(flavor, compact = false) {
      const image = this.element('img', compact ? 'box-mix__selection-photo' : 'box-mix__photo');
      Object.assign(image, {src: flavor.image, alt: compact ? '' : this.text('photo_alt', {flavor: this.displayName(flavor), description: flavor.description}), width: 480, height: 360, loading: 'lazy', decoding: 'async'});
      return image;
    }
    renderRows() {
      this.list.replaceChildren();
      this.selections.replaceChildren();
      this.config.flavors.forEach((flavor) => {
        const id = flavor.id;
        const row = this.element('li', 'box-mix__card');
        row.dataset.flavorCard = id;
        row.dataset.mixRow = id;
        row.setAttribute('aria-labelledby', `BoxMixName-${this.dataset.sectionId}-${id}`);
        const visual = this.element('div', 'box-mix__visual');
        const badge = this.element('span', 'box-mix__badge', this.text('selected_badge'));
        badge.dataset.mixBadge = '';
        badge.hidden = true;
        visual.append(this.photo(flavor), badge);
        const body = this.element('div', 'box-mix__card-body');
        const name = this.element('h3', 'box-mix__name', this.displayName(flavor));
        name.id = `BoxMixName-${this.dataset.sectionId}-${id}`;
        const description = this.element('p', 'box-mix__description', flavor.description);
        description.id = `BoxMixDescription-${this.dataset.sectionId}-${id}`;
        const add = this.button('box-mix__add', this.text('increase', {flavor: this.displayName(flavor)}), 'increase', id, this.text('add'));
        add.dataset.mixAdd = '';
        const warning = this.element('p', 'box-mix__row-error', this.text('incompatible'));
        warning.dataset.mixIncompatible = '';
        warning.hidden = true;
        const quantity = this.element('p', 'box-mix__pieces-per-kg', flavor.quantityText);
        body.append(name, description, quantity, this.coatingControls(flavor, 'card'));
        if (this.coatingOptions(flavor).length === 1) body.append(add, this.controls(flavor, 'card'));
        body.append(warning);
        row.append(visual, body);
        this.list.append(row);
        const selected = this.element('li', 'box-mix__selection');
        selected.dataset.mixRow = id;
        selected.dataset.mixSelection = id;
        selected.hidden = true;
        const selectionBody = this.element('div', 'box-mix__selection-body');
        const selectionName = this.element('p', 'box-mix__selection-name', this.displayName(flavor));
        const selectionHead = this.element('div', 'box-mix__selection-heading');
        selectionHead.append(selectionName, this.button('box-mix__remove', this.text('remove', {flavor: this.displayName(flavor)}), 'remove', id, '×'));
        const selectionWarning = warning.cloneNode(true);
        selectionBody.append(selectionHead, this.coatingControls(flavor, 'tray'));
        if (this.coatingOptions(flavor).length === 1) selectionBody.append(this.controls(flavor, 'tray'));
        selectionBody.append(selectionWarning);
        selected.append(this.photo(flavor, true), selectionBody);
        this.selections.append(selected);
      });
    }
    matchesFilter(flavor) {
      if (this.filter === 'selected') return this.hasFlavor(flavor.id);
      return this.filter === 'all' || flavor.categories?.includes(this.filter);
    }
    filterCards() {
      const query = this.search.value.trim().toLocaleLowerCase();
      const matches = this.config.flavors.filter((flavor) => (this.allowed(flavor) || this.hasFlavor(flavor.id)) && this.matchesFilter(flavor) && `${flavor.id} ${flavor.name} ${flavor.description}`.toLocaleLowerCase().includes(query));
      this.resultCount = matches.length;
      const visible = new Set(matches.map((flavor) => flavor.id));
      this.list.querySelectorAll('[data-flavor-card]').forEach((card) => { card.hidden = !visible.has(card.dataset.flavorCard); });
      this.querySelector('[data-mix-no-results]').hidden = matches.length > 0;
      this.querySelector('[data-mix-results]').textContent = this.text('results', {count: matches.length});
    }
    handleClick(event) {
      const link = event.target.closest('a[data-mix-return]');
      if (link) return this.returnToDetails(event, link);
      const target = event.target.closest('button');
      if (!target || !this.contains(target) || target.disabled) return;
      if (target.hasAttribute('data-mix-view')) return this.setView(target.dataset.mixView, true);
      if (target.hasAttribute('data-mix-filter') || target.hasAttribute('data-mix-clear-filters')) {
        this.filter = target.dataset.mixFilter || 'all';
        if (target.hasAttribute('data-mix-clear-filters')) this.search.value = '';
        this.querySelectorAll('[data-mix-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mixFilter === this.filter)));
        this.filterCards();
        if (target.hasAttribute('data-mix-clear-filters')) this.search.focus();
        return;
      }
      if (target.hasAttribute('data-mix-reset')) {
        this.allocations.clear();
        this.update();
        this.querySelector('[data-mix-tray] h3').focus({preventScroll: true});
        return;
      }
      const id = target.dataset.flavorId;
      const flavor = this.config.flavors.find((item) => item.id === id);
      if (!flavor) return;
      const action = target.dataset.mixAction;
      const coating = target.dataset.coating || this.coatingOptions(flavor)[0];
      if (!this.coatingOptions(flavor).includes(coating)) return;
      const key = this.portionKey(id, coating);
      const current = Number(this.allocations.get(key) || 0);
      if (action === 'remove') {
        this.coatingOptions(flavor).forEach((option) => this.allocations.delete(this.portionKey(id, option)));
      } else if (action === 'decrease') {
        if (current <= 100 || !Number.isFinite(current)) this.allocations.delete(key);
        else this.allocations.set(key, String(Math.max(0, Math.ceil(current / 100) * 100 - 100)));
      } else if (action === 'increase') {
        const status = this.status();
        if (!this.allowed(flavor) || !Number.isFinite(current) || current % 100 || current + 100 > this.maxPerFlavor || status.total + 100 > this.targetWeight || (!this.hasFlavor(id) && status.flavorCount >= this.maxFlavors)) return;
        this.allocations.set(key, String(current + 100));
      } else return;
      this.update();
      const row = target.closest('[data-mix-row]');
      if (row?.hidden) this.querySelector('[data-mix-tray] h3').focus({preventScroll: true});
      else if (target.hidden || target.disabled || target.closest('[hidden]')) {
        const add = row?.querySelector(`[data-mix-coating-add="${key}"]`);
        const portion = row?.querySelector(`[data-mix-portion="${key}"]`);
        const candidates = [add, portion?.querySelector('.box-mix__step[data-mix-action="increase"]'), portion?.querySelector('.box-mix__step[data-mix-action="decrease"]'), row?.querySelector('[data-mix-add]'), row?.querySelector('[data-mix-action="remove"]')];
        const next = candidates.find((button) => button && !button.disabled && !button.closest('[hidden]'));
        next?.focus({preventScroll: true});
      }
    }
    setProperty(key, value, enabled = true) {
      const input = this.querySelector(`[data-mix-property="${key}"]`);
      input.value = value;
      input.disabled = !enabled;
    }
    syncSubmitControl(status) {
      const blocked = this.nativeSubmit?.disabled || this.nativeSubmit?.getAttribute('aria-disabled') === 'true';
      const busy = this.nativeSubmit?.classList.contains('loading');
      this.summarySubmit.disabled = !status.valid || !!blocked || !!busy;
      this.summarySubmit.querySelector('span').textContent = this.text(busy ? 'adding' : 'add_box');
      this.summarySubmit.setAttribute('aria-busy', String(!!busy));
    }
    update() {
      if (this.updating) return this.status();
      this.updating = true;
      if (this.mode === 'custom') this.selectNativeVariant(this.requiredVariant());
      const status = this.status();
      this.dataset.valid = String(status.valid);
      this.form.dataset.boxMixValid = String(status.valid);
      if (this.submitGate) this.submitGate.disabled = !status.valid;
      this.syncSubmitControl(status);
      this.querySelector('[data-mix-progress]').textContent = this.text('progress_short', {grams: status.total});
      this.querySelector('[data-mix-count]').textContent = this.text('flavor_count', {count: status.flavorCount});
      this.querySelector('[data-mix-filter-count]').textContent = String(status.flavorCount);
      this.querySelector('[data-mix-price]').textContent = status.variant?.price || '';
      const currentType = this.querySelector('[data-mix-current-type]');
      if (currentType) currentType.textContent = this.mode === 'custom' ? '' : status.variant?.title || '';
      this.querySelector('[data-mix-meter]').value = Math.max(0, Math.min(this.targetWeight, status.total));
      this.querySelector('[data-mix-remaining]').textContent = status.total === this.targetWeight ? this.text('complete') : this.text(status.total < this.targetWeight ? 'remaining' : 'over', {grams: Math.abs(this.targetWeight - status.total)});
      this.querySelector('[data-mix-dock-status]').textContent = this.text('dock_status', {grams: status.total, count: status.flavorCount});
      this.querySelector('[data-mix-reset]').disabled = !this.allocations.size;
      this.querySelector('[data-mix-tray-empty]').hidden = this.allocations.size > 0;
      this.querySelectorAll('[data-mix-row]').forEach((row) => {
        const id = row.dataset.mixRow;
        const flavor = this.config.flavors.find((item) => item.id === id);
        const selected = this.hasFlavor(id);
        const allowed = this.allowed(flavor);
        row.querySelectorAll('[data-mix-portion]').forEach((controls) => {
          const key = controls.dataset.mixPortion;
          const raw = this.allocations.get(key) ?? '0';
          const grams = Number(raw);
          const display = controls.querySelector('[data-mix-grams]');
          if (display.textContent !== raw) display.textContent = raw;
          const validAmount = raw !== '' && Number.isInteger(grams) && grams >= 0 && grams <= this.maxPerFlavor && grams % 100 === 0;
          controls.setAttribute('aria-invalid', String(!validAmount || (selected && !allowed)));
          controls.querySelector('[data-mix-action="decrease"]').disabled = !this.allocations.has(key) || grams === 0;
          controls.querySelector('[data-mix-action="increase"]').disabled = !allowed || status.total >= this.targetWeight || grams >= this.maxPerFlavor || !validAmount || (!selected && status.flavorCount >= this.maxFlavors);
          const add = controls.querySelector('[data-mix-coating-add]');
          if (add) {
            const zero = validAmount && grams === 0;
            const increase = controls.querySelector('.box-mix__step[data-mix-action="increase"]');
            add.hidden = !zero;
            add.disabled = increase.disabled;
            controls.querySelectorAll('.box-mix__step, .box-mix__amount').forEach((part) => { part.hidden = zero; });
          }
        });
        row.dataset.selected = String(selected);
        row.querySelector('[data-mix-incompatible]').hidden = !selected || allowed;
        if (row.hasAttribute('data-mix-selection')) row.hidden = !selected;
        else {
          row.querySelector('[data-mix-badge]').hidden = !selected;
          const add = row.querySelector('[data-mix-add]');
          if (add) {
            add.hidden = selected;
            add.disabled = !allowed || status.total >= this.targetWeight || (!selected && status.flavorCount >= this.maxFlavors);
            row.querySelector('.box-mix__controls').hidden = !selected;
          }
        }
      });
      this.filterCards();
      this.setProperty('selection', this.text(this.mode === 'custom' ? 'custom_value' : 'elsa'));
      this.setProperty('type', this.mode === 'custom' ? this.text('per_piece_type') : status.variant?.title || '', !!status.variant);
      this.setProperty('weight', `${this.targetWeight} g`);
      const customValid = this.mode === 'custom' && status.valid;
      const ordered = status.selected.slice().sort((a, b) => Number(a.flavor?.id) - Number(b.flavor?.id) || this.coatingOptions(a.flavor).indexOf(a.coating) - this.coatingOptions(b.flavor).indexOf(b.coating));
      this.setProperty('flavors', customValid ? ordered.map(({flavor, coating, grams}) => `${flavor.id}. ${flavor.name} — ${this.coatingLabel(flavor, coating)}: ${grams} g`).join('; ') : '', customValid);
      this.setProperty('signature', customValid ? `menu-v10-dual|${status.variant.id}|${ordered.map(({flavor, coating, grams}) => `${flavor.id}:${coating}:${grams}`).join(',')}` : '', customValid);
      const visibleError = status.error && !['remaining', 'over'].includes(status.error);
      this.showError(visibleError ? this.text(status.error) : '');
      this.updating = false;
      this.dispatchEvent(new CustomEvent('boxmix:change', {bubbles: true, detail: {valid: status.valid, total: status.total, mode: this.mode, flavors: status.flavorCount}}));
      return status;
    }
    showError(message, focus = false) {
      this.error.textContent = message;
      this.error.hidden = !message;
      if (message && focus) this.error.focus();
    }
    validateSubmit(event) {
      const status = this.update();
      const busy = this.nativeSubmit?.classList.contains('loading') || this.nativeSubmit?.getAttribute('aria-disabled') === 'true';
      if (status.valid && !this.nativeSubmit?.disabled && !busy) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) this.showError(this.text(status.error || 'invalid_variant', {grams: Math.abs(this.targetWeight - status.total)}), true);
    }
  }
  customElements.define('box-mix-builder', BoxMixBuilder);
})();



